import React, { FunctionComponent } from 'react';
import StartIcon from '@mui/icons-material/Start';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';

import { PropertySelectField } from '@paragon/client-tools';
import { Component, LineLocation, RelativeBearingGeometry, RelativeMemberGeometry } from '@paragon/contract-library';
import { findAndCalculateLine, getDirectionalLineLocationTerms } from '@paragon/relative-geometry';

import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { useAppSelector } from '../../../hooks';
import { SceneMenuFragment } from '../../../misc/menus/common/SceneMenuFragment';
import { SceneMenuItem } from '../../../misc/menus/common/SceneMenuItem';

interface Props {
  readonly parentObject?: string;
  readonly relativeGeometry: RelativeMemberGeometry | RelativeBearingGeometry;
  readonly submit: (value: LineLocation) => void | Promise<void>;
  readonly excludeCenter?: boolean;
  readonly disabled: boolean;
  readonly clickMenu?: boolean;
}

const lineLocations: LineLocation[] = ['Center', 'Port', 'Starboard'];
const swappedLineLocations: LineLocation[] = ['Center', 'Starboard', 'Port'];

const getVirtualDefinitionLineDirection = (
  component: Component,
  relativeGeometry: RelativeMemberGeometry | RelativeBearingGeometry,
) =>
  relativeGeometry.geometryType === 'Bearing'
    ? // Bearings are a special case because their definition line does not determine port/starboard; their bearing line does.
      // See https://github.com/ParagonTruss/ParagonCore/pull/8170 for more details.
      findAndCalculateLine(relativeGeometry.bearingLineGuid, component)?.direction.rotateReverseQuarterTurn()
    : findAndCalculateLine(relativeGeometry.lineGuid, component)?.direction;

export const LineLocationSelect: FunctionComponent<Props> = ({
  parentObject,
  relativeGeometry,
  submit,
  excludeCenter,
  disabled,
  clickMenu,
}) => {
  const component = useAppSelector(getActiveComponentDesign)?.component;
  const definitionLineDirection = component && getVirtualDefinitionLineDirection(component, relativeGeometry);
  const { terms, swapPortAndStarboardOrdering } = getDirectionalLineLocationTerms(definitionLineDirection ?? null);
  const options = (swapPortAndStarboardOrdering ? swappedLineLocations : lineLocations).filter(
    (lineLocation) => !excludeCenter || lineLocation !== 'Center',
  );
  if (clickMenu) {
    return (
      <SceneMenuFragment>
        {options.map((lineLocation) => (
          <SceneMenuItem
            key={lineLocation}
            disabled={disabled}
            selected={relativeGeometry.lineLocation === lineLocation}
            text={`${terms[lineLocation]} ${parentObject ?? 'Line'}`}
            icon={
              (terms['Port'] === 'Above' || terms['Port'] === 'Below') && terms[lineLocation] === 'Centered On' ? (
                <VerticalAlignCenterIcon />
              ) : (terms['Port'] === 'Right Of' || terms['Port'] === 'Left Of') &&
                terms[lineLocation] === 'Centered On' ? (
                <VerticalAlignCenterIcon sx={{ transform: 'rotate(90deg)' }} />
              ) : terms[lineLocation] === 'Below' ? (
                <StartIcon sx={{ transform: 'rotate(90deg)' }} />
              ) : terms[lineLocation] === 'Above' ? (
                <StartIcon sx={{ transform: 'rotate(-90deg)' }} />
              ) : terms[lineLocation] === 'Left Of' ? (
                <StartIcon sx={{ transform: 'rotate(180deg)' }} />
              ) : (
                <StartIcon />
              )
            }
            onClick={() => submit(lineLocation)}
          />
        ))}
      </SceneMenuFragment>
    );
  }
  return (
    <PropertySelectField
      sx={{ m: 1 }}
      property="Location"
      options={options}
      value={relativeGeometry.lineLocation}
      getName={(lineLocation) => `${terms[lineLocation]} ${parentObject ?? 'Line'}`}
      submit={submit}
      disabled={disabled}
    />
  );
};
